import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { handleFetchUnreadCount, handleMarkSeen, handleReceiveLiveNotification } from 'redux/actions/notifications'
import { handleNotificationClick } from 'services/notifications/handleNotificationClick'
import { INotification, NavigateFn } from 'services/notifications/types'
import { IToastEntry } from 'components/Notifications/NotificationToastStack'
import { normalizeNotification } from 'services/notifications/normalizeNotification'
import { getNotificationSemanticKey } from 'services/notifications/notificationIdentity'

const EXIT_ANIMATION_MS = 160
const MAX_VISIBLE_TOASTS = 5
const isDev = process.env.NODE_ENV === 'development'

export const getToastKey = (notification: INotification) =>
  getNotificationSemanticKey(notification) ||
  notification.chrono_key ||
  notification.notif_id ||
  `${notification.service}:${notification.event_type}`

// Receives postMessage broadcasts from public/push-sw.js:
// - 'push-notification-click': the OS notification was clicked with the app already open in some
//   tab (see push-sw.js's notificationclick handler) — resolve and navigate right here.
// - 'push-notification-received': a push arrived while the tab was open and focused, so the SW
//   deliberately skipped showing an OS notification — hand it to the caller to render as a toast.
export function usePushNotificationMessages(navigate: NavigateFn) {
  const dispatch = useDispatch()
  const [toasts, setToasts] = useState<IToastEntry[]>([])

  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.map((t) => (t.id === toastId ? { ...t, exiting: true } : t)))
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== toastId))
    }, EXIT_ANIMATION_MS)
  }, [])

  const markSeen = useCallback(
    (notification: INotification) => {
      dispatch(handleMarkSeen({ seenKeys: [notification.chrono_key] }))
    },
    [dispatch]
  )

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'push-debug') {
        if (isDev) {
          console.info('[WebPush][SW]', event.data.message, {
            version: event.data.version,
            ...(event.data.details ?? {}),
          })
        }
        return
      }

      if (event.data?.type === 'push-notification-click') {
        const notification: INotification = normalizeNotification(event.data.notification)
        if (isDev) console.info('[WebPush] push notification click received from service worker', notification)
        // Insert into the Redux inbox/unread lists first so the Notification Center has this item
        // at all, and so the markSeen dispatch below actually has something to flip optimistically
        // instead of being a no-op on a chrono_key it's never seen.
        dispatch(handleReceiveLiveNotification(notification))
        handleNotificationClick(notification, navigate, { markSeen, dispatch }).catch((err) =>
          console.warn('[Notifications] click handling failed:', err)
        )
        return
      }

      if (event.data?.type === 'push-notification-received') {
        const notification: INotification = normalizeNotification(event.data.notification)
        if (isDev) console.info('[WebPush] focused push received; rendering in-app toast', notification)
        dispatch(handleFetchUnreadCount())
        dispatch(handleReceiveLiveNotification(notification))
        const toastKey = getToastKey(notification)

        setToasts((prev) => {
          const existingIndex = prev.findIndex((toast) => toast.id === toastKey)
          if (existingIndex >= 0) {
            return prev.map((toast, index) =>
              index === existingIndex ? { ...toast, notification, exiting: false } : toast
            )
          }

          return [{ id: toastKey, notification }, ...prev].slice(0, MAX_VISIBLE_TOASTS)
        })
      }
    }

    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [dispatch, markSeen, navigate])

  return { toasts, dismissToast, markSeen }
}
